import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Grid, Paper, Title, Text, Button, Group, Stack, Badge, ScrollArea,
  ActionIcon, Divider, TextInput, Select, Loader, Center, Tabs,
  Modal,
} from "@mantine/core";
import { IconPlus, IconMinus, IconTrash, IconCash, IconBuildingBank, IconUserPlus, IconUser, IconRefresh, IconHelp } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import HttpClient from "../helpers/HttpClient";
import { Article } from "../types/article.type";
import { ConsumationOrder } from "../types/consumation-order.type";
import { useLanguage } from "../providers/LanguageContext";
import { startRestaurantTour, startRestaurantTourOnFirstVisit } from "../helpers/restaurantTour";

// getAll() on BaseService returns T[] directly (no { status, data } wrapper)
// getOrders() on ConsumationOrderService returns { status, data } (custom method)
type OrdersResponse = { status: number; data: ConsumationOrder[] };
type BackendItem<T> = { status: number; data: T };

type DraftPosition = { article: Article; quantity: number; unitPrice: number };
type DraftOrder = {
  id?: number;
  person?: { id: number; firstName: string; lastName: string };
  guestName?: string;
  positions: DraftPosition[];
  isMember: boolean;
};

const nowGuestName = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyDraft = (): DraftOrder => ({ positions: [], isMember: false, guestName: nowGuestName() });
const formatCurrency = (v: number) => v.toFixed(2).replace(".", ",") + " €";
const orderTotal = (positions: DraftPosition[]) =>
  positions.reduce((sum, p) => sum + p.quantity * p.unitPrice, 0);
const orderLabel = (o: DraftOrder | ConsumationOrder) => {
  if (o.person) return `${o.person.firstName} ${o.person.lastName}`;
  if (o.guestName) return o.guestName;
  return "Unbekannt";
};

// The server already returns restaurant articles sorted by category, then name —
// split that list into consecutive per-category sections without re-sorting.
const groupByCategory = (articles: Article[]) => {
  const groups: { key: string; name: string | null; articles: Article[] }[] = [];
  for (const article of articles) {
    const key = article.category ? String(article.category.id) : "none";
    const last = groups[groups.length - 1];
    if (last?.key === key) last.articles.push(article);
    else groups.push({ key, name: article.category?.name ?? null, articles: [article] });
  }
  return groups;
};

const RestaurantPOS: React.FC = () => {
  const { t } = useLanguage();
  const articleClient = useRef(new HttpClient<Article>("/article")).current;
  const orderClient = useRef(new HttpClient<ConsumationOrder>("/consumation-order")).current;
  const personClient = useRef(new HttpClient<any>("/person")).current;

  const [articles, setArticles] = useState<Article[]>([]);
  // Stock of stock-tracked restaurant articles ({ articleId: stock }); the POS
  // only warns about empty stock, it never blocks a sale.
  const [stock, setStock] = useState<Record<number, number>>({});
  const [openOrders, setOpenOrders] = useState<ConsumationOrder[]>([]);
  const [activeOrderId, setActiveOrderId] = useState<string>("new");
  const [draft, setDraft] = useState<DraftOrder>(emptyDraft());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [persons, setPersons] = useState<{ id: number; firstName: string; lastName: string }[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [guestNameInput, setGuestNameInput] = useState("");
  const [personMode, setPersonMode] = useState<"member" | "guest">("member");

  const fetchOpenOrders = useCallback(async () => {
    try {
      // consumation-order uses custom getOrders → returns { status, data }
      const res = await orderClient.getAll({ status: "open" }) as unknown as OrdersResponse;
      setOpenOrders(res.data ?? []);
    } catch {
      // non-fatal: open orders just stay empty
    }
  }, [orderClient]);

  const loadStock = useCallback(async () => {
    try {
      const res = await new HttpClient<Record<number, number>>("/inventory/restaurant-stock").getSingle();
      setStock(res ?? {});
    } catch {
      // Non-fatal: without stock data the POS simply shows no stock hints.
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // BaseService.getAll() returns T[] directly — no .data wrapper
      const [articleList, personList] = await Promise.all([
        articleClient.getAll({ restaurant: 'true' }) as unknown as Article[],
        personClient.getAll({ slim: 'true' }) as unknown as { id: number; firstName: string; lastName: string }[],
      ]);
      setArticles(articleList ?? []);
      setPersons(personList ?? []);
      await fetchOpenOrders();
      await loadStock();
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [articleClient, personClient, fetchOpenOrders, loadStock]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Anchors only exist once loading is done, so start the tour after that.
  useEffect(() => {
    if (!loading && !loadError) startRestaurantTourOnFirstVisit();
  }, [loading, loadError]);

  const loadExistingOrder = (order: ConsumationOrder) => {
    setDraft({
      id: order.id,
      person: order.person,
      guestName: order.guestName,
      isMember: !!order.person,
      positions: (order.positions ?? []).map((p: any) => ({
        article: p.article,
        quantity: p.quantity,
        unitPrice: Number(p.unitPrice),
      })),
    });
    if (order.person) {
      setPersonMode("member");
      setSelectedPersonId(String(order.person.id));
    } else if (order.guestName) {
      setPersonMode("guest");
      setGuestNameInput(order.guestName);
    }
  };

  const handleTabChange = (value: string | null) => {
    if (!value) return;
    setActiveOrderId(value);
    if (value === "new") {
      setDraft(emptyDraft());
      setSelectedPersonId(null);
      setGuestNameInput("");
    } else {
      const order = openOrders.find((o) => String(o.id) === value);
      if (order) loadExistingOrder(order);
    }
  };

  const addArticle = (article: Article) => {
    const price = draft.isMember && article.memberPrice != null
      ? article.memberPrice
      : (article.unitPrice ?? 0);
    const inStock = stock[article.id];
    if (inStock !== undefined) {
      const alreadyInOrder = draft.positions.find((p) => p.article.id === article.id)?.quantity ?? 0;
      if (alreadyInOrder + 1 > inStock) {
        notifications.show({
          color: "yellow",
          title: `${article.name}: laut Bestand nicht mehr vorrätig`,
          message: `Bestand: ${inStock}. Der Artikel wird trotzdem hinzugefügt – bitte Bestand prüfen.`,
          autoClose: 3000,
        });
      }
    }
    setDraft((prev) => {
      const idx = prev.positions.findIndex((p) => p.article.id === article.id);
      if (idx >= 0) {
        const updated = [...prev.positions];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
        return { ...prev, positions: updated };
      }
      return { ...prev, positions: [...prev.positions, { article, quantity: 1, unitPrice: price }] };
    });
  };

  const resetToNewOrder = () => {
    setActiveOrderId("new");
    setDraft(emptyDraft());
    setSelectedPersonId(null);
    setGuestNameInput("");
  };

  // A saved tab whose last item was removed is deleted instead of lingering
  // as an empty tab.
  const deleteEmptyTab = async (orderId: number) => {
    try {
      const res = await orderClient.delete(orderId);
      if (res.status !== 200) throw new Error();
      notifications.show({ message: "Leerer Tab entfernt", color: "blue", autoClose: 1500 });
      resetToNewOrder();
      await fetchOpenOrders();
    } catch {
      notifications.show({ message: "Tab konnte nicht entfernt werden", color: "red" });
    }
  };

  const updatePositions = (update: (positions: DraftPosition[]) => DraftPosition[]) => {
    const updated = update([...draft.positions]);
    if (updated.length === 0 && draft.id) {
      deleteEmptyTab(draft.id);
      return;
    }
    setDraft((prev) => ({ ...prev, positions: updated }));
  };

  const changeQty = (idx: number, delta: number) => {
    updatePositions((positions) => {
      const newQty = positions[idx].quantity + delta;
      if (newQty <= 0) positions.splice(idx, 1);
      else positions[idx] = { ...positions[idx], quantity: newQty };
      return positions;
    });
  };

  const removePosition = (idx: number) => {
    updatePositions((positions) => {
      positions.splice(idx, 1);
      return positions;
    });
  };

  const confirmPersonSelection = () => {
    if (personMode === "member") {
      const found = persons.find((p) => String(p.id) === selectedPersonId);
      if (!found) {
        notifications.show({ message: "Person nicht gefunden", color: "red" });
        return;
      }
      setDraft((prev) => ({ ...prev, person: found, guestName: undefined, isMember: true }));
    } else {
      const name = guestNameInput.trim();
      if (!name) {
        notifications.show({ message: "Bitte Gastnamen eingeben", color: "red" });
        return;
      }
      setDraft((prev) => ({ ...prev, guestName: name, person: undefined, isMember: false }));
    }
    setPersonModalOpen(false);
  };

  const saveDraft = async (): Promise<number | null> => {
    const payload: any = {
      id: draft.id,
      person: draft.person ? { id: draft.person.id } : null,
      guestName: draft.guestName ?? null,
      orderDate: new Date().toISOString(),
      positions: draft.positions.map((p) => ({
        article: { id: p.article.id },
        quantity: p.quantity,
        unitPrice: p.unitPrice,
      })),
    };
    const res = await orderClient.post(payload) as unknown as BackendItem<ConsumationOrder>;
    const saved = res.data;
    if (!saved) return null;
    await fetchOpenOrders();
    return saved.id;
  };

  const handlePayCash = async () => {
    setSaving(true);
    try {
      const id = await saveDraft();
      if (!id) throw new Error();
      await orderClient.postAction(`${id}/pay-cash`);
      notifications.show({ message: "Barzahlung erfasst", color: "green" });
      loadStock();
      await fetchOpenOrders();
      setActiveOrderId("new");
      setDraft(emptyDraft());
      setSelectedPersonId(null);
      setGuestNameInput("");
    } catch {
      notifications.show({ message: "Fehler beim Speichern", color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const handleBookToAccount = async () => {
    if (!draft.person) return;
    setSaving(true);
    try {
      const id = await saveDraft();
      if (!id) throw new Error();
      await orderClient.postAction(`${id}/book-to-account`);
      notifications.show({ message: "Auf Konto gebucht", color: "green" });
      loadStock();
      await fetchOpenOrders();
      setActiveOrderId("new");
      setDraft(emptyDraft());
      setSelectedPersonId(null);
      setGuestNameInput("");
    } catch {
      notifications.show({ message: "Fehler beim Buchen", color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTab = async () => {
    setSaving(true);
    try {
      const id = await saveDraft();
      if (!id) throw new Error();
      setDraft((prev) => ({ ...prev, id }));
      setActiveOrderId(String(id));
      notifications.show({ message: "Tab gespeichert", color: "blue" });
    } catch {
      notifications.show({ message: "Fehler beim Speichern", color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const total = orderTotal(draft.positions);
  const hasPerson = !!(draft.person || draft.guestName);
  const hasPositions = draft.positions.length > 0;
  const articleGroups = useMemo(() => groupByCategory(articles), [articles]);
  const personSelectData = useMemo(
    () => persons.map((p) => ({ value: String(p.id), label: `${p.lastName} ${p.firstName}` })),
    [persons],
  );

  if (loading) return <Center mt="xl"><Loader /></Center>;
  if (loadError) return (
    <Center mt="xl">
      <Stack align="center">
        <Text c="red">Daten konnten nicht geladen werden.</Text>
        <Button onClick={loadAll}>Erneut versuchen</Button>
      </Stack>
    </Center>
  );

  return (
    <>
      <Modal
        opened={personModalOpen}
        onClose={() => setPersonModalOpen(false)}
        title="Wer bestellt?"
        size="auto"
        centered
      >
        <Stack pb="md">
          <Group grow>
            <Button
              variant={personMode === "member" ? "filled" : "light"}
              leftSection={<IconUser size={18} />}
              onClick={() => setPersonMode("member")}
              size="lg"
            >
              Mitglied
            </Button>
            <Button
              variant={personMode === "guest" ? "filled" : "light"}
              leftSection={<IconUserPlus size={18} />}
              onClick={() => setPersonMode("guest")}
              size="lg"
            >
              Gast
            </Button>
          </Group>

          {personMode === "member" ? (
            <Select
              label="Mitglied auswählen"
              placeholder="Name suchen…"
              searchable
              clearable
              value={selectedPersonId}
              onChange={setSelectedPersonId}
              data={personSelectData}
              size="lg"
            />
          ) : (
            <TextInput
              label="Gastname"
              placeholder="Name des Gastes"
              value={guestNameInput}
              onChange={(e) => setGuestNameInput(e.currentTarget.value)}
              size="lg"
            />
          )}

          <Button onClick={confirmPersonSelection} size="lg">Bestätigen</Button>
        </Stack>
      </Modal>

      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={3}>Restaurant – Kasse</Title>
          <Group gap="xs">
            <Button
              leftSection={<IconHelp size={16} />}
              variant="subtle"
              onClick={startRestaurantTour}
            >
              Anleitung
            </Button>
            <Button
              data-tour="new-order"
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={() => {
                const fresh = emptyDraft();
                setActiveOrderId("new");
                setDraft(fresh);
                setSelectedPersonId(null);
                setGuestNameInput(fresh.guestName ?? '');
                notifications.show({ message: "Neuer Auftrag bereit", color: "blue", autoClose: 1500 });
              }}
            >
              Neuer Auftrag
            </Button>
          </Group>
        </Group>

        {openOrders.length > 0 && (
          <Tabs value={activeOrderId} onChange={handleTabChange}>
            <Tabs.List>
              <Tabs.Tab value="new">+ Neu</Tabs.Tab>
              {openOrders.map((o) => (
                <Tabs.Tab key={o.id} value={String(o.id)}>{orderLabel(o)}</Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}

        <Grid>
          {/* Left: current order */}
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <Paper withBorder p="md">
              <Stack>
                <Group justify="space-between" align="center">
                  <Text fw={600} size="lg">
                    {hasPerson
                      ? draft.person
                        ? `${draft.person.firstName} ${draft.person.lastName}`
                        : draft.guestName
                      : "Kein Kunde gewählt"}
                  </Text>
                  {hasPerson && (
                    <Badge color={draft.isMember ? "blue" : "gray"}>
                      {draft.isMember ? "Mitglied" : "Gast"}
                    </Badge>
                  )}
                  <ActionIcon data-tour="customer" variant="light" onClick={() => { setGuestNameInput(draft.guestName ?? ''); setPersonModalOpen(true); }} title="Kunde wählen" size="lg">
                    <IconUser size={18} />
                  </ActionIcon>
                </Group>

                <Divider />

                <ScrollArea mah={300} data-tour="positions">
                  {draft.positions.length === 0 ? (
                    <Text c="dimmed" ta="center" py="lg" size="sm">
                      Artikel antippen um sie hinzuzufügen
                    </Text>
                  ) : (
                    <Stack gap="xs">
                      {draft.positions.map((pos, idx) => (
                        <Group key={idx} justify="space-between" wrap="nowrap">
                          <Text size="sm" style={{ flex: 1 }}>{pos.article.name}</Text>
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon size="sm" variant="light" onClick={() => changeQty(idx, -1)}><IconMinus size={12} /></ActionIcon>
                            <Text size="sm" w={24} ta="center">{pos.quantity}</Text>
                            <ActionIcon size="sm" variant="light" onClick={() => changeQty(idx, 1)}><IconPlus size={12} /></ActionIcon>
                          </Group>
                          <Text size="sm" w={72} ta="right">{formatCurrency(pos.quantity * pos.unitPrice)}</Text>
                          <ActionIcon size="sm" color="red" variant="subtle" onClick={() => removePosition(idx)}><IconTrash size={12} /></ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </ScrollArea>

                <Divider />
                <Group justify="space-between" data-tour="total">
                  <Text fw={700} size="xl">Gesamt</Text>
                  <Text fw={700} size="xl">{formatCurrency(total)}</Text>
                </Group>

                <Stack gap="sm" data-tour="payment">
                  <Button size="xl" leftSection={<IconCash size={20} />} color="green" disabled={!hasPositions || saving} loading={saving} onClick={handlePayCash} fullWidth>
                    Barzahlung
                  </Button>

                  {draft.isMember && (
                    <Button size="xl" leftSection={<IconBuildingBank size={20} />} color="blue" variant="light" disabled={!hasPositions || saving} loading={saving} onClick={handleBookToAccount} fullWidth>
                      Auf Konto buchen
                    </Button>
                  )}

                  {hasPositions && (
                    <Button size="md" variant="subtle" leftSection={<IconRefresh size={16} />} disabled={saving} onClick={handleSaveTab} fullWidth>
                      {draft.id ? "Tab aktualisieren" : "Tab offen lassen"}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Grid.Col>

          {/* Right: article grid */}
          <Grid.Col span={{ base: 12, sm: 7 }}>
            <Paper withBorder p="md" data-tour="articles">
              <Title order={5} mb="md">Artikel</Title>
              {articles.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  Keine Restaurant-Artikel vorhanden. Bitte Artikel unter „Rechnungen → Artikel" als Restaurant-Artikel markieren.
                </Text>
              ) : (
                <Stack gap="md">
                  {articleGroups.map((group) => (
                    <div key={group.key}>
                      {articleGroups.length > 1 && (
                        <Text size="sm" fw={700} c="dimmed" tt="uppercase" mb="xs">
                          {group.name ?? t.article.uncategorized}
                        </Text>
                      )}
                      <Grid>
                        {group.articles.map((article) => {
                          const price = draft.isMember && article.memberPrice != null
                            ? article.memberPrice
                            : (article.unitPrice ?? 0);
                          return (
                            <Grid.Col key={article.id} span={{ base: 6, xs: 4 }}>
                              <Paper
                                withBorder
                                p="sm"
                                style={{
                                  cursor: "pointer",
                                  textAlign: "center",
                                  minHeight: 80,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  userSelect: "none",
                                }}
                                onClick={() => addArticle(article)}
                              >
                                <Text size="sm" fw={600} ta="center" style={{ lineHeight: 1.3 }}>
                                  {article.name}
                                </Text>
                                <Text size="xs" c="dimmed" mt={4}>{formatCurrency(price)}</Text>
                                {stock[article.id] !== undefined && (
                                  <Text size="xs" c={stock[article.id] <= 0 ? "red" : "dimmed"} fw={stock[article.id] <= 0 ? 700 : 400}>
                                    Bestand: {stock[article.id]}
                                  </Text>
                                )}
                              </Paper>
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                    </div>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
};

export default RestaurantPOS;
